import { MIN_TRADE_AMOUNT, PIRATE_TOKEN_LOOT_POINTS_FRACTION, PIRATE_TOKEN_MIN_DAILY_CAP, PIRATE_TOKEN_MIN_LOOT, PIRATE_TOKEN_POINTS_UNIT, PIRATE_TOKEN_TIER_MULTIPLIER, PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS, PIRATE_TOKEN_WINDOW_MS, BUILDING_NAMES, RESOURCE_NAMES, TRADE_PAIRS, TRADE_WINDOW_HOURS, TRADE_WINDOW_MS, TRADES_PER_LEVEL_PER_WINDOW, } from "@kaeliux/shared";
import { QueueError } from "./tickEvents.js";
// ---------------------------------------------------------------------------
// Shared epoch-floor windowing core, reused by every GLOBAL (server-dictated,
// identical-for-every-player — NOT a per-actor rolling reset like
// `scrapRecycleDaily`) window this game has: the Market's own
// trade-spend cap below, and the pirate-flight token-EARN anti-farming cap.
// Kept private/numeric; each window gets its own thin, appropriately-typed
// wrapper (Date for `Planet`-side fields, epoch-ms number for `PlayerPremium`
// fields, matching that type's own `PremiumTierGrant.expiresAt` convention).
// ---------------------------------------------------------------------------
function windowStartMs(nowMs, windowMs) {
    return Math.floor(nowMs / windowMs) * windowMs;
}
function windowCount(recorded, nowMs, windowMs) {
    if (!recorded)
        return 0;
    return recorded.windowStart === windowStartMs(nowMs, windowMs) ? recorded.count : 0;
}
function findTradePair(a, b) {
    const pair = TRADE_PAIRS.find((p) => (p.pricier === a && p.cheaper === b) || (p.pricier === b && p.cheaper === a));
    if (!pair) {
        // Reachable with a===b (no self-pair is ever defined) — a bare
        // `Error` here would surface as an unmapped 500 server-side and an
        // uncaught crash client-side, instead of a normal rejection.
        // `TradeResourcesSchema.refine(from !== to)` already blocks this at
        // the endpoint, but `calculateTradeOutput` is also called directly
        // from `TradeCenterPage`'s render path, so this is defense in depth.
        throw new QueueError("failed-precondition", `Cannot trade ${a} for ${b === a ? "itself" : b}`);
    }
    return pair;
}
/** Resource received for trading away `amount` of `from`, at the Market's sell/buy spread — see {@link TRADE_PAIRS}. */
export function calculateTradeOutput(from, to, amount) {
    const pair = findTradePair(from, to);
    return from === pair.pricier
        ? Math.floor(amount * pair.sellRate)
        : Math.floor(amount / pair.buyCost);
}
/**
 * Start instant of the global trade-count window containing `now` — a pure
 * function of wall-clock time alone (epoch-floor division), so every
 * planet and every player computing it for the same instant get the
 * identical boundary. Unlike `scrapRecycleDaily`'s rolling reset, no
 * planet's own trade history shifts this.
 */
export function currentTradeWindowStart(now) {
    return new Date(windowStartMs(now.getTime(), TRADE_WINDOW_MS));
}
/** Trades already made in the CURRENT global window — 0 if `tradeWindow` is absent or belongs to an earlier (stale) window. */
export function resolveTradeWindowCount(tradeWindow, now) {
    if (!tradeWindow)
        return 0;
    return windowCount({ windowStart: tradeWindow.windowStart.getTime(), count: tradeWindow.count }, now.getTime(), TRADE_WINDOW_MS);
}
/**
 * Start instant (epoch-ms, matching `PlayerPremium`'s own timestamp
 * convention — see `PremiumTierGrant.expiresAt`) of the global pirate-token
 * earn window containing `now`. Same epoch-floor construction as
 * {@link currentTradeWindowStart}, just a different window length and a
 * different (player-scoped, not planet-scoped) owner.
 */
export function currentPirateTokenWindowStart(now) {
    return windowStartMs(now.getTime(), PIRATE_TOKEN_WINDOW_MS);
}
/** Trade tokens already earned from Pirate flights in the CURRENT earn window — 0 if `window` is absent or stale. */
export function resolvePirateTokenWindowCount(window, now) {
    return windowCount(window, now.getTime(), PIRATE_TOKEN_WINDOW_MS);
}
/**
 * Minimum resources (metal+minerals+gas) a won Pirate flight must loot to
 * earn a trade token: the higher of a flat floor
 * (`PIRATE_TOKEN_MIN_LOOT`, protects a low-points player) and a fraction of
 * the raider's OWN resource score points (`PIRATE_TOKEN_LOOT_POINTS_FRACTION`,
 * discourages farming trivial raids once a player's economy has outgrown
 * the floor). Applied in `resolveBattleMission` (`fleetResolution.ts`)
 * alongside the existing roll/outcome gate.
 */
export function calculatePirateTokenLootThreshold(resourcePoints) {
    return Math.max(resourcePoints * PIRATE_TOKEN_LOOT_POINTS_FRACTION, PIRATE_TOKEN_MIN_LOOT);
}
/**
 * The pirate-token earn-window cap for a player with `points` **total**
 * score (resource + battle — see `PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS`'s
 * doc comment for why total rather than resource-only), scaled by their
 * effective premium tier — `PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS` tokens
 * per `PIRATE_TOKEN_POINTS_UNIT` points, times `PIRATE_TOKEN_TIER_MULTIPLIER`,
 * floored to a whole number of tokens, then floored again at
 * `PIRATE_TOKEN_MIN_DAILY_CAP` so a low-points player — most of the
 * playerbase this mechanic serves — still has a reachable cap rather than a
 * literal 0 below ~100,000 points.
 */
export function calculatePirateTokenDailyCap(points, tier) {
    const base = (points / PIRATE_TOKEN_POINTS_UNIT) * PIRATE_TOKEN_TOKENS_PER_MILLION_POINTS;
    return Math.max(PIRATE_TOKEN_MIN_DAILY_CAP, Math.floor(base * PIRATE_TOKEN_TIER_MULTIPLIER[tier]));
}
/**
 * Apply a Pirate-flight token roll to a player's premium state, enforcing
 * `dailyCap` (see {@link calculatePirateTokenDailyCap}) — the anti-farming
 * cap on how many trade tokens a player may EARN (as opposed to buy or
 * spend) per window. Without this cap, a Pirate flight (Vanguard vs. an
 * undefended target, no cooldown, no minimum opposition) would be an
 * unlimited token source, since tokens are the only volume gate on trading.
 *
 * `grantedByRoll` is the raw 0/1 output of `resolveBattleMission`'s roll
 * (`FleetArrivalResolution.tradeTokensGranted`) — this function is what
 * decides whether that roll's token actually gets credited. A no-op
 * (`grantedByRoll <= 0`, or the window is already at `dailyCap`) returns
 * `premium` unchanged; the window is only advanced when a token is
 * actually credited, so raids that roll no reward (or lose) never consume
 * earn-window capacity.
 */
export function applyPirateTokenGrant(premium, grantedByRoll, dailyCap, now) {
    if (grantedByRoll <= 0)
        return premium;
    const count = resolvePirateTokenWindowCount(premium.pirateTokenWindow, now);
    if (count >= dailyCap)
        return premium;
    return {
        ...premium,
        tradeTokens: premium.tradeTokens + grantedByRoll,
        pirateTokenWindow: { windowStart: currentPirateTokenWindowStart(now), count: count + 1 },
    };
}
/**
 * Execute a resource trade: validates the Market's global window
 * capacity and the `from` balance, deducts `from`/credits `to` (capacity-
 * clamped) at the exchange rate, and advances `tradeWindow`. Throws
 * `QueueError` on any rejection.
 *
 * Token-balance validation/deduction is NOT done here — that balance lives
 * on the player doc, outside `Planet` — so the caller (the Cloud Function)
 * checks it before calling this. This function only owns planet-local
 * state (resources + window).
 */
export function planResourceTrade(planet, tradeCenterLevel, input, now) {
    if (tradeCenterLevel <= 0) {
        throw new QueueError("failed-precondition", `${BUILDING_NAMES.tradeCenter} is not built on this planet`);
    }
    const windowStart = currentTradeWindowStart(now);
    const count = resolveTradeWindowCount(planet.tradeWindow, now);
    const limit = tradeCenterLevel * TRADES_PER_LEVEL_PER_WINDOW;
    if (count >= limit) {
        throw new QueueError("queue-full", `Trade window is full (${limit} trade${limit === 1 ? "" : "s"} per ${TRADE_WINDOW_HOURS}h)`);
    }
    const { from, to, amount } = input;
    // Defense in depth — TradeResourcesSchema already enforces this at the
    // endpoint, but this function has no other guard against a caller (or a
    // future direct/test call site) passing a sub-minimum amount.
    if (amount < MIN_TRADE_AMOUNT) {
        throw new QueueError("failed-precondition", `Minimum trade amount is ${MIN_TRADE_AMOUNT}`);
    }
    if (amount > planet.resources[from].current) {
        throw new QueueError("insufficient-resources", `Insufficient ${RESOURCE_NAMES[from]} for this trade`);
    }
    const output = calculateTradeOutput(from, to, amount);
    const toResource = planet.resources[to];
    const credited = Math.min(toResource.current + output, toResource.capacity) - toResource.current;
    // Paying the full `from` amount, a token, and a window slot for zero
    // received resources is never a valid trade. In practice this only
    // fires when `to` is already at (or nearly at) capacity — with
    // MIN_TRADE_AMOUNT and the current TRADE_PAIRS rates, a raw `output`
    // of 0 can't happen (even the steepest buy rate, 1.3, yields
    // floor(100 / 1.3) = 76 at the minimum amount) — but the check stays
    // general rather than assuming that always holds. The client's own
    // capacity-waste check (TradeCenterPage) is a UX convenience, not a
    // substitute for this — it can't see a tick that fills storage between
    // render and click, so the server has to be the actual authority.
    if (credited <= 0) {
        throw new QueueError("failed-precondition", `${RESOURCE_NAMES[to]} storage is already full — this trade would yield nothing`);
    }
    let resources = planet.resources;
    resources = { ...resources, [from]: { ...resources[from], current: resources[from].current - amount } };
    resources = {
        ...resources,
        [to]: { ...resources[to], current: toResource.current + credited },
    };
    return {
        ...planet,
        resources,
        tradeWindow: { windowStart, count: count + 1 },
        updatedAt: now,
    };
}
//# sourceMappingURL=trade.js.map